import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
// Custom PDF document icon
const PdfIcon = ({
  className = 'w-5 h-5'
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-666">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-667" />
    <polyline points="14 2 14 8 20 8" data-id="element-668" />
    <text x="12" y="17.5" textAnchor="middle" fontSize="5.5" fontWeight="700" stroke="none" fill="currentColor" style={{
    fontFamily: 'system-ui, sans-serif'
  }} data-id="element-669">
      PDF
    </text>
  </svg>;
const MAR_APR_2026_PDF_URL = 'https://www.dropbox.com/scl/fi/70ann28c3s73okmsqd7g4/Mar-2026-Apr-2026.pdf?rlkey=xvc35ht60pycz4dv1exyyxv69&st=xfpsw8pl';
interface MarApr2026IssuePageProps {
  language: Language;
}
export function MarApr2026IssuePage({
  language
}: MarApr2026IssuePageProps) {
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="pt-20 md:pt-24 bg-gray-50 min-h-screen pb-0" data-id="element-670">
      {/* Section 1: Issue Header */}
      <section className="relative bg-[#1a5c5c] py-8 md:py-12 px-6 overflow-hidden" data-id="element-671">
        <svg className="absolute right-0 top-0 w-1/2 h-full pointer-events-none opacity-5" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMid meet" viewBox="0 0 1200 400" data-id="element-672">
          <g fill="white" data-id="element-673">
            <path d="M820,145 C810,130 795,120 780,115 C790,125 795,135 800,142 C790,138 775,132 760,130 C778,138 790,142 800,145 C795,148 790,155 785,165 C795,155 800,148 805,145 C810,148 815,155 820,165 C815,155 812,148 810,145 C820,142 835,138 850,130 C835,132 820,138 810,142 C815,135 820,125 830,115 Z" transform="translate(200,50) scale(1.5)" data-id="element-674" />
          </g>
        </svg>

        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center" data-id="element-675">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-white/20 shadow-xl mb-6 flex-shrink-0 bg-white" data-id="element-676">
            <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-677" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className={`text-3xl md:text-5xl font-bold text-white mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-678">
            {isPashto ? 'مارچ – اپریل ۲۰۲۶' : 'March – April 2026'}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className={`text-white/70 text-lg md:text-xl font-medium tracking-wide ${isPashto ? 'font-pashto' : ''}`} data-id="element-679">
            {isPashto ? 'ټوک ۲۱' : 'Volume 21'}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex w-48 md:w-64 h-1.5 mt-8 rounded-full overflow-hidden" data-id="element-680">
            <div className="flex-1 bg-[#C62828]" data-id="element-681"></div>
            <div className="flex-1 bg-[#F9A825]" data-id="element-682"></div>
            <div className="flex-1 bg-[#2E7D32]" data-id="element-683"></div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Featured Article Block */}
      <section className="py-8 md:py-12 px-6" data-id="element-684">
        <div className="max-w-3xl mx-auto" data-id="element-685">
          <motion.div {...fadeIn} className="bg-white rounded-2xl shadow-xl shadow-gray-200/40 border border-gray-100 p-8 md:p-12" data-id="element-686">
            <h2 className={`text-2xl md:text-3xl font-bold text-pasoon-dark text-center mb-8 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-687">
              {isPashto ? 'ټولنیز کار یو مقدس امانت دی' : 'Community Work is a Sacred Trust'}
            </h2>

            <div className="space-y-8" data-id="element-688">
              {isPashto ? <>
                  <div className="text-right" dir="rtl" data-id="element-689">
                    <p className="text-gray-700 font-pashto leading-loose text-lg" data-id="element-690">
                      ټولنیز کار شخصي ملکیت نه دی. دا د ټولنې ګډ امانت دی. کله
                      چې د شخصي ګټې لپاره وکارول شي، خپل بشپړتیا له لاسه ورکوي
                      او عامه باور کمزوری کوي. که څه هم یو فرد یې پیل کولی شي،
                      خو یوازې د ټولنې یووالی او ژمنتیا کولی شي چې بشپړ یې کړي.
                      ریښتینی پرمختګ یوه شپه نه کیږي. دا وخت، صبر او اخلاص ته
                      اړتیا لري. عزم، یووالی او استقامت د دوامداره بریالیتوب
                      بنسټونه دي.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-691"></div>
                  <div className="text-left" dir="ltr" data-id="element-692">
                    <p className="text-gray-600 leading-relaxed text-base" data-id="element-693">
                      Community work is not personal property. It is a shared
                      trust of the community. When used for personal gain, it
                      loses its integrity and weakens public trust. While one
                      individual may initiate it, only the unity and commitment
                      of the community can bring it to completion. True progress
                      does not happen overnight. It requires time, patience, and
                      sincerity. Determination, unity, and perseverance remain
                      the foundations of lasting success.
                    </p>
                  </div>
                </> : <>
                  <div className="text-left" dir="ltr" data-id="element-694">
                    <p className="text-gray-700 leading-relaxed text-lg" data-id="element-695">
                      Community work is not personal property. It is a shared
                      trust of the community. When used for personal gain, it
                      loses its integrity and weakens public trust. While one
                      individual may initiate it, only the unity and commitment
                      of the community can bring it to completion. True progress
                      does not happen overnight. It requires time, patience, and
                      sincerity. Determination, unity, and perseverance remain
                      the foundations of lasting success.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-696"></div>
                  <div className="text-right" dir="rtl" data-id="element-697">
                    <p className="text-gray-600 font-pashto leading-loose text-base" data-id="element-698">
                      ټولنیز کار شخصي ملکیت نه دی. دا د ټولنې ګډ امانت دی. کله
                      چې د شخصي ګټې لپاره وکارول شي، خپل بشپړتیا له لاسه ورکوي
                      او عامه باور کمزوری کوي. که څه هم یو فرد یې پیل کولی شي،
                      خو یوازې د ټولنې یووالی او ژمنتیا کولی شي چې بشپړ یې کړي.
                      ریښتینی پرمختګ یوه شپه نه کیږي. دا وخت، صبر او اخلاص ته
                      اړتیا لري. عزم، یووالی او استقامت د دوامداره بریالیتوب
                      بنسټونه دي.
                    </p>
                  </div>
                </>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: Cover Image Display */}
      <section className="py-8 md:py-10 px-6 bg-gray-100" data-id="element-699">
        <div className="max-w-4xl mx-auto text-center" data-id="element-700">
          <motion.div {...fadeIn} className="inline-block bg-white p-3 md:p-4 rounded-xl shadow-lg shadow-gray-300/50 border border-gray-200" data-id="element-701">
            <img src="https://cdn.magicpatterns.com/uploads/sR4i2r4S1futQxXXFLun8U/Mar-April_26.jpg" alt="Pasoon March-April 2026 Cover" className="w-full max-w-2xl h-auto object-contain rounded" data-id="element-702" />
          </motion.div>
          <motion.p {...fadeIn} className={`mt-6 text-gray-500 text-sm md:text-base font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-703">
            {isPashto ? 'ګورنمنټ پوسټ ګریجویټ کالج مردان' : 'Government Post Graduate College Mardan'}
          </motion.p>
        </div>
      </section>

      {/* Section 5: Call to Action */}
      <section className="py-8 md:py-12 px-6 bg-white" data-id="element-704">
        <div className="max-w-3xl mx-auto" data-id="element-705">
          <motion.div {...fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6" data-id="element-706">
            <a href={`${MAR_APR_2026_PDF_URL}&dl=1`} className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-xl font-bold text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-707">
              <PdfIcon className="w-5 h-5" data-id="element-708" />
              {isPashto ? 'PDF ډاونلوډ کړئ' : 'Download PDF'}
            </a>

            <Link to="/subscription" className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-4 text-pasoon-muted font-bold text-lg hover:text-green-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-709">
              <Heart className="w-5 h-5" data-id="element-710" />
              {isPashto ? 'پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Issue Information Footer Block */}
      <section className="bg-[#1a1a1a] relative" data-id="element-711">
        <div className="flex w-full h-1.5" data-id="element-712">
          <div className="flex-1 bg-[#C62828]" data-id="element-713"></div>
          <div className="flex-1 bg-[#F9A825]" data-id="element-714"></div>
          <div className="flex-1 bg-[#2E7D32]" data-id="element-715"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10 md:py-12" data-id="element-716">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8" data-id="element-717">
            <div className="text-center md:text-start space-y-3" data-id="element-718">
              <p className={`text-white/90 font-bold text-lg ${isPashto ? 'font-pashto' : ''}`} data-id="element-719">
                {isPashto ? 'خپرندوی: پاڅون خپرندویه ادارې، مردان' : 'Published by: Pasoon Publishers, Mardan'}
              </p>
              <div className="space-y-1 text-white/60 text-sm" data-id="element-720">
                <p data-id="element-721">Tel & Fax: 0937 – 874489</p>
                <p data-id="element-722">Email: pasoonmardan@gmail.com</p>
                <p data-id="element-723">Website: www.pasoon.org</p>
              </div>
            </div>

            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/10 bg-white flex-shrink-0" data-id="element-724">
              <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-725" />
            </div>
          </div>
        </div>
      </section>
    </div>;
}